<script>
  import { afterUpdate, getContext } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link } from 'svelte-navigator';
  import { Breadcrumb, BreadcrumbItem, Button, Col, Container, Row } from '@sveltestrap/sveltestrap';
  import { createQuery } from '@tanstack/svelte-query';

  import { EmptyState } from '@client/components/empty-state';
  import { Loader } from '@client/components/loader';
  import { getNotificationsContext } from '@client/components/notifications';
  import { PluginCard } from '@client/components/plugin-card';
  import { RenameModal } from '@client/components/rename-modal';
  import { Tour } from '@client/components/tour';

  import { PluginsService } from '@client/services/plugins';

  import { integrationsSettingsStore } from '@client/stores/page-settings';

  import { setTourStoreSettings } from '@client/utils/tours';

  import { CREATE_STEPS } from './tours/create';
  import { EDIT_STEPS } from './tours/edit';
  import { FULL_STEPS } from './tours/full';
  import { OVERVIEW_STEPS } from './tours/overview';
  import { FormModal, ProviderModal } from './modals';

  const { basepath } = getContext('config');
  const { addNotification } = getNotificationsContext();

  const queryParams = {};

  let fallbacktext = '';
  let parameters = [];
  let provider = '';
  let rulename = '';

  let showIntegrationFormModal = false;
  let showIntegrationProviderModal = false;
  let showRenameModal = false;
  let renamePlugin = null;

  let originalRecordCount = 0;
  let initialized = false;

  const query = createQuery({
    refetchOnWindowFocus: false,
    queryKey: ['plugins', queryParams],
    queryFn: async () => {
      const response = await PluginsService.getAll();

      if (!initialized) {
        initialized = true;
        originalRecordCount = response.length;
      }

      if (response.length === 0) {
        originalRecordCount = 0;
      }

      return response;
    }
  });

  const onAddIntegration = () => (showIntegrationProviderModal = true);

  const onRenameUpdate = async (plugin, newName) => {
    const response = await PluginsService.savePlugin({ ...plugin, rulename: newName });

    if (response?.status >= 400) {
      addNotification({
        text: $format('label.FAILURE.INTEGRATION_RENAME', { values: { rulename: plugin.rulename } }),
        type: 'error',
        autohide: 2500,
        position: 'top-right'
      });
      return;
    }

    addNotification({
      text: $format('label.SUCCESS.RENAME', { values: { from: plugin.rulename, to: newName } }),
      type: 'success',
      autohide: 2500,
      position: 'top-right'
    });

    showRenameModal = false;
    $query.refetch();
  };

  afterUpdate(() => {
    $integrationsSettingsStore = setTourStoreSettings($integrationsSettingsStore, !!data.length);
  });

  $: hasData = $integrationsSettingsStore?.tours?.hasData;

  $: integrationsTourSteps = [
    {
      steps: OVERVIEW_STEPS,
      stepsKey: 'overview',
      menuLabel: $format('label.TOURS.INTEGRATIONS.MENU.OVERVIEW'),
      storeKey: 'overviewHasToured',
      disabled: !hasData
    },
    {
      steps: CREATE_STEPS,
      stepsKey: 'default',
      menuLabel: $format('label.TOURS.INTEGRATIONS.MENU.CREATE'),
      storeKey: 'createHasToured',
      disabled: false
    },
    {
      steps: EDIT_STEPS,
      stepsKey: 'edit',
      menuLabel: $format('label.TOURS.INTEGRATIONS.MENU.EDIT'),
      storeKey: 'editHasToured',
      disabled: !hasData
    },
    {
      steps: FULL_STEPS,
      stepsKey: 'full'
    }
  ];

  $: dismissedOrToured = $integrationsSettingsStore?.tours?.isDismissed || $integrationsSettingsStore?.tours?.hasToured;
  $: hasIntegrations = initialized && originalRecordCount > 0;
  $: data = Array.isArray($query.data) ? [...$query.data].sort((a, b) => a?.provider?.localeCompare(b?.provider)) : [];
</script>

<div id="plugins" class="page" class:with-footer={$query.data?.data}>
  <Loader backdrop show={$query.isLoading || $query.isFetching} />
  <div class="page-header">
    <Container fluid>
      <Row>
        <Col md="12">
          <Breadcrumb>
            <BreadcrumbItem>
              <a href="{basepath}/manage" use:link>{$format('label.MANAGE')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem active>{$format('label.INTEGRATIONS')}</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row class="flex-align--center">
        <Col sm="7" md="7" xs="6">
          <h1 class="page-title">{$format('label.INTEGRATIONS')}</h1>
        </Col>
        <Col sm="5" md="5" xs="6" class="text-right">
          {#if dismissedOrToured && hasIntegrations}
            <Tour steps={integrationsTourSteps} store={integrationsSettingsStore} />
          {/if}

          <Button id="new-integration-btn" color="secondary" on:click={onAddIntegration}>
            {$format('label.ADD_INTEGRATION')}
          </Button>
        </Col>
      </Row>
    </Container>
  </div>
  <div class="page-content">
    {#if !dismissedOrToured && hasIntegrations}
      <Tour
        steps={integrationsTourSteps}
        store={integrationsSettingsStore}
        tourHeader={$format('label.TOURS.INTEGRATIONS.ALERT.HEADER')}
        tourDescription={$format('label.TOURS.INTEGRATIONS.ALERT.DESCRIPTION')}
      />
    {/if}
    {#if hasIntegrations}
      <div class="plugin-cards">
        {#each data as plugin}
          <PluginCard
            data={plugin}
            onRename={(p) => {
              renamePlugin = p;
              showRenameModal = true;
            }}
          />
        {/each}
      </div>
    {:else if initialized}
      <EmptyState
        icon="package"
        heading={$format('label.EMPTY_STATE.INTEGRATION_HEADING')}
        description={$format('label.EMPTY_STATE.INTEGRATION_DESCRIPTION')}
        action={$format('label.EMPTY_STATE.INTEGRATION_ACTION')}
        on:click={onAddIntegration}
      />
    {/if}
  </div>
</div>

<RenameModal
  bind:show={showRenameModal}
  data={renamePlugin}
  name={renamePlugin?.rulename ?? ''}
  existingNames={data.map((p) => p.rulename)}
  duplicateError={$format('label.RENAME_MODAL.ERROR.DUPLICATE_INTEGRATION')}
  onUpdate={onRenameUpdate}
/>

<ProviderModal bind:show={showIntegrationProviderModal} bind:provider bind:showNextModal={showIntegrationFormModal} />

<FormModal
  bind:data
  bind:show={showIntegrationFormModal}
  bind:fallbacktext
  bind:parameters
  bind:provider
  bind:rulename
  bind:showPreviousModal={showIntegrationProviderModal}
/>

<style lang="scss" src="./integrations.scss"></style>
